// src/components/HermodsPath/LuxAssetBrowser.js
// Browsable grid of Lux Libris visual assets for Hermod's Path

import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import { getAllAssets } from '../../services/luxLibrisAssets';

const CATEGORIES = [
  { id: 'saints', label: 'Saints', count: 251 },
  { id: 'badges', label: 'Bird Badges', count: 52 },
  { id: 'luxlings', label: 'Luxlings', count: 16 },
  { id: 'bookshelves', label: 'Bookshelves', count: 23 },
  { id: 'trophyCases', label: 'Trophy Cases', count: 23 },
  { id: 'janeAusten', label: 'Jane Austen', count: 4 },
  { id: 'readingDna', label: 'Reading DNA', count: 6 },
  { id: 'genreCovers', label: 'Genre Covers', count: 24 },
  { id: 'logos', label: 'Logos', count: 3 },
];

export default function LuxAssetBrowser({ onSelectAsset }) {
  const [expanded, setExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('saints');
  const [searchQuery, setSearchQuery] = useState('');

  const assets = useMemo(() => getAllAssets(), []);

  const filteredItems = useMemo(() => {
    const items = assets[activeCategory] || [];
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item => item.name.toLowerCase().includes(q));
  }, [assets, activeCategory, searchQuery]);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full mt-6 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded-xl p-4 text-left transition flex items-center justify-between"
      >
        <div>
          <h3 className="text-sm font-bold text-slate-200">Browse Lux Libris Assets</h3>
          <p className="text-xs text-slate-400 mt-0.5">Saints, badges, luxlings, bookshelves, and more</p>
        </div>
        <ChevronDown size={16} className="text-slate-400" />
      </button>
    );
  }

  return (
    <div className="mt-6 bg-slate-700/30 border border-slate-600 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(false)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition"
      >
        <h3 className="text-sm font-bold text-slate-200">Browse Lux Libris Assets</h3>
        <ChevronUp size={16} className="text-slate-400" />
      </button>

      {/* Category tabs */}
      <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => { setActiveCategory(cat.id); setSearchQuery(''); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
              activeCategory === cat.id
                ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                : 'bg-slate-700/50 text-slate-400 hover:text-slate-300 border border-transparent'
            }`}
          >
            {cat.label}
            <span className="ml-1 opacity-60">{cat.count}</span>
          </button>
        ))}
      </div>

      {/* Search (for large categories) */}
      {(activeCategory === 'saints' || activeCategory === 'badges' || activeCategory === 'genreCovers') && (
        <div className="px-4 py-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${CATEGORIES.find(c => c.id === activeCategory)?.label.toLowerCase()}...`}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-800 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
            />
          </div>
        </div>
      )}

      {/* Asset grid */}
      <div className="px-4 pb-4 max-h-80 overflow-y-auto">
        {filteredItems.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-6">No assets found</p>
        ) : (
          <div className={`grid gap-2 ${
            activeCategory === 'bookshelves' || activeCategory === 'trophyCases'
              ? 'grid-cols-3'
              : 'grid-cols-4 sm:grid-cols-5 md:grid-cols-6'
          }`}>
            {filteredItems.map((item, i) => (
              <button
                key={item.url || i}
                onClick={() => onSelectAsset(item.url, item.name)}
                className="group relative aspect-square rounded-lg overflow-hidden border border-slate-600 hover:border-sky-400 transition bg-slate-800"
                title={item.name}
              >
                <img
                  src={item.url}
                  alt={item.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                  <p className="text-[10px] text-white leading-tight truncate">{item.name}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
